import { createLogger } from '@ondit/logger'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { requestJson } from '@/lib/api/client/request'
import {
  removeWorkspaceEnvironmentContract,
  upsertWorkspaceEnvironmentContract,
} from '@/lib/api/contracts/environment'
import type { ContractBodyInput } from '@/lib/api/contracts/types'
import type { WorkspaceEnvironmentData } from '@/lib/environment/api'
import { fetchPersonalEnvironment, fetchWorkspaceEnvironment } from '@/lib/environment/api'
import { invalidateSelectorQueries } from '@/hooks/queries/utils/selector-keys'

const logger = createLogger('EnvironmentQueries')

/**
 * Query key factories for environment variable queries
 */
export const PERSONAL_ENVIRONMENT_STALE_TIME = 60 * 1000
export const WORKSPACE_ENVIRONMENT_STALE_TIME = 60 * 1000

export const environmentKeys = {
  all: ['environment'] as const,
  personal: () => [...environmentKeys.all, 'personal'] as const,
  workspaces: () => [...environmentKeys.all, 'workspace'] as const,
  workspace: (workspaceId: string) => [...environmentKeys.workspaces(), workspaceId] as const,
}

/**
 * Hook to fetch personal environment variables
 */
export function usePersonalEnvironment(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: environmentKeys.personal(),
    queryFn: ({ signal }) => fetchPersonalEnvironment(signal),
    enabled: options?.enabled ?? true,
    staleTime: PERSONAL_ENVIRONMENT_STALE_TIME,
    // Pinned off (not inheriting the desktop QueryClient default): the secrets
    // manager seeds an editable form from this data, so a background focus
    // refetch during a concurrent edit would drop the user's unsaved rows.
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch workspace environment variables
 */
export function useWorkspaceEnvironment<TData = WorkspaceEnvironmentData>(
  workspaceId: string,
  options?: { enabled?: boolean; select?: (data: WorkspaceEnvironmentData) => TData }
) {
  return useQuery({
    queryKey: environmentKeys.workspace(workspaceId),
    queryFn: ({ signal }) => fetchWorkspaceEnvironment(workspaceId, signal),
    enabled: Boolean(workspaceId) && (options?.enabled ?? true),
    staleTime: WORKSPACE_ENVIRONMENT_STALE_TIME,
    // See usePersonalEnvironment: seeds an editable form, so a focus refetch
    // during a concurrent workspace-env edit must not clobber unsaved rows.
    refetchOnWindowFocus: false,
    select: options?.select,
  })
}

/**
 * Upsert workspace environment variables mutation
 */
type UpsertWorkspaceEnvironmentParams = { workspaceId: string } & ContractBodyInput<
  typeof upsertWorkspaceEnvironmentContract
>

export function useUpsertWorkspaceEnvironment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ workspaceId, variables }: UpsertWorkspaceEnvironmentParams) => {
      const data = await requestJson(upsertWorkspaceEnvironmentContract, {
        params: { id: workspaceId },
        body: { variables },
      })
      logger.info(`Upserted workspace environment variables for workspace: ${workspaceId}`)
      return data
    },
    onSettled: (_data, _error, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: environmentKeys.workspace(variables.workspaceId),
        }),
        invalidateSelectorQueries(queryClient),
      ]),
  })
}

/**
 * Remove workspace environment variables mutation
 */
type RemoveWorkspaceEnvironmentParams = { workspaceId: string } & ContractBodyInput<
  typeof removeWorkspaceEnvironmentContract
>

export function useRemoveWorkspaceEnvironment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ workspaceId, keys }: RemoveWorkspaceEnvironmentParams) => {
      const data = await requestJson(removeWorkspaceEnvironmentContract, {
        params: { id: workspaceId },
        body: { keys },
      })
      logger.info(`Removed ${keys.length} workspace environment keys for workspace: ${workspaceId}`)
      return data
    },
    onSettled: (_data, _error, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: environmentKeys.workspace(variables.workspaceId),
        }),
        invalidateSelectorQueries(queryClient),
      ]),
  })
}
